/**
 * Saves the Desktop's arrangement to the server — one saver per workspace.
 *
 * The rules (plan § Phase 2, "How concurrent saves work"):
 *   - Changes apply locally at once and are sent after a quiet spell
 *     (debounced), coalesced per key: the last edit to an icon wins.
 *   - ONE request in flight at a time. Everything that piles up meanwhile is
 *     folded into the next request, so an older retry can never land after a
 *     newer edit — sending one at a time is the fix, not per-key versions.
 *   - The server merges and returns the whole layout; that becomes the base,
 *     with anything still unsent drawn on top of it.
 *   - A failed save keeps the local change, shows "not saved yet" and
 *     retries with backoff. A `LAYOUT_LIMIT_EXCEEDED` refusal stops retrying
 *     and asks the person to reset the arrangement.
 *   - The saver is created for ONE holder and captures it: nothing reads
 *     "the current holder" at send time, so a workspace switch can't send
 *     one workspace's arrangement to another. `flush()` sends now (switching
 *     workspace, leaving the page, hiding the tab); a request already in
 *     flight finishes against the holder it started with.
 */
import {
  applyPatch,
  emptyLayout,
  emptyPatch,
  isEmptyPatch,
  mergePatches,
  patchRequests,
  type DesktopLayout,
  type LayoutPatch,
  type LayoutPatchRequest,
} from './desktop-layout';

export type LayoutSaveStatus = 'saved' | 'pending' | 'saving' | 'retrying' | 'limit';

export interface LayoutSaverTransport {
  patch: (
    holderId: string,
    body: LayoutPatchRequest & { baseRevision: number },
  ) => Promise<{ layout: DesktopLayout; revision: number }>;
}

export interface LayoutSaverOptions {
  debounceMs?: number;
  retryBaseMs?: number;
  retryMaxMs?: number;
  /** Recognises the server's "too many arranged items" refusal. */
  isLimitError?: (err: unknown) => boolean;
}

const DEFAULTS = { debounceMs: 600, retryBaseMs: 2_000, retryMaxMs: 30_000 };
/** Retries a disposed saver still makes for changes it was holding — a
 *  workspace switch or leaving the Desktop must not drop an arrangement
 *  because the network blipped at that moment. */
const RETRIES_AFTER_DISPOSE = 3;

export class DesktopLayoutSaver {
  readonly holderId: string;
  private readonly transport: LayoutSaverTransport;
  private readonly options: Required<Omit<LayoutSaverOptions, 'isLimitError'>> &
    Pick<LayoutSaverOptions, 'isLimitError'>;
  private base: DesktopLayout = emptyLayout();
  private revision = 0;
  private pending: LayoutPatch = emptyPatch();
  private inflight: LayoutPatch | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay: number;
  private status: LayoutSaveStatus = 'saved';
  private listeners = new Set<() => void>();
  private snapshot: { layout: DesktopLayout; status: LayoutSaveStatus } = {
    layout: emptyLayout(),
    status: 'saved',
  };
  private disposed = false;
  private retriesAfterDispose = 0;

  constructor(holderId: string, transport: LayoutSaverTransport, options: LayoutSaverOptions = {}) {
    this.holderId = holderId;
    this.transport = transport;
    this.options = { ...DEFAULTS, ...options };
    this.retryDelay = this.options.retryBaseMs;
  }

  /** The server's layout, as loaded. Unsent local changes stay on top. */
  setBase(layout: DesktopLayout, revision: number): void {
    // An in-flight response is newer than a refetch that started before it.
    if (revision < this.revision) return;
    this.base = layout;
    this.revision = revision;
    this.emit();
  }

  /** What to draw: the server's layout with every unsent change applied. */
  getSnapshot = (): { layout: DesktopLayout; status: LayoutSaveStatus } => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Apply `patch` locally now, and save it soon. */
  enqueue(patch: LayoutPatch): void {
    if (isEmptyPatch(patch)) return;
    this.pending = mergePatches(this.pending, patch);
    if (this.status === 'saved' || this.status === 'limit') this.status = 'pending';
    this.emit();
    this.schedule(this.options.debounceMs);
  }

  /** Send whatever is pending now instead of waiting out the delay. */
  flush(): void {
    this.clearDebounce();
    void this.send();
  }

  /** (Re)start after `dispose` — React may unmount and remount the same
   *  saver (StrictMode). */
  activate(): void {
    this.disposed = false;
    this.retriesAfterDispose = 0;
  }

  /** Stop timers. Pending changes are flushed first. */
  dispose(): void {
    this.flush();
    this.disposed = true;
    this.clearRetry();
  }

  private schedule(ms: number): void {
    if (this.disposed) return;
    this.clearDebounce();
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.send();
    }, ms);
  }

  private async send(): Promise<void> {
    if (this.inflight || isEmptyPatch(this.pending)) return;
    this.clearRetry();
    const sending = this.pending;
    this.pending = emptyPatch();
    this.inflight = sending;
    this.status = 'saving';
    this.emit();
    try {
      for (const body of patchRequests(sending)) {
        const result = await this.transport.patch(this.holderId, {
          ...body,
          baseRevision: this.revision,
        });
        this.base = result.layout;
        this.revision = Math.max(this.revision, result.revision);
      }
      this.inflight = null;
      this.retryDelay = this.options.retryBaseMs;
      this.status = isEmptyPatch(this.pending) ? 'saved' : 'pending';
      this.emit();
      if (!isEmptyPatch(this.pending)) {
        // Disposed mid-flight (workspace switched, page left): what was edited
        // meanwhile still belongs to this holder — send it now.
        if (this.disposed) void this.send();
        else this.schedule(0);
      }
    } catch (err) {
      // Keep the change: it goes back under anything edited since.
      this.pending = mergePatches(sending, this.pending);
      this.inflight = null;
      if (this.options.isLimitError?.(err)) {
        this.status = 'limit';
        this.emit();
        return;
      }
      this.status = 'retrying';
      this.emit();
      // A disposed saver still owns what it was holding, for ITS holder: it
      // keeps trying a few times rather than dropping the change.
      if (this.disposed) {
        if (this.retriesAfterDispose >= RETRIES_AFTER_DISPOSE) return;
        this.retriesAfterDispose += 1;
      }
      const delay = this.retryDelay;
      this.retryDelay = Math.min(this.retryDelay * 2, this.options.retryMaxMs);
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        void this.send();
      }, delay);
    }
  }

  private clearDebounce(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private emit(): void {
    let layout = this.base;
    if (this.inflight) layout = applyPatch(layout, this.inflight);
    layout = applyPatch(layout, this.pending);
    this.snapshot = { layout, status: this.status };
    for (const listener of this.listeners) listener();
  }
}
